import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { AppShell, useShell } from '../components/AppShell';
import { normaliseTheme, resolveAccent } from '../themes';
import {
  SUBDOMAIN_RE,
  MAX_UPLOAD_BYTES,
  UK_POSTCODE_RE,
  normaliseReviews,
  DAY_ORDER,
  normaliseOpeningHours,
  openingHoursToDisplay,
  emptyReview,
} from '../lib/shopProfile/helpers';
import { IdentityPanel } from '../components/shopProfile/panels/IdentityPanel';
import { ThemePickerPanel } from '../components/shopProfile/panels/ThemePickerPanel';
import { LogoBrandPanel } from '../components/shopProfile/panels/LogoBrandPanel';
import { HeroImagePanel } from '../components/shopProfile/panels/HeroImagePanel';
import { BookingPagePanel } from '../components/shopProfile/panels/BookingPagePanel';
import { GalleryPanel } from '../components/shopProfile/panels/GalleryPanel';
import { ReviewsPanel } from '../components/shopProfile/panels/ReviewsPanel';
import { LocationPanel } from '../components/shopProfile/panels/LocationPanel';
import { BusinessHoursPanel } from '../components/shopProfile/panels/BusinessHoursPanel';
import { CancellationPolicyPanel } from '../components/shopProfile/panels/CancellationPolicyPanel';
import { TogglesPanel } from '../components/shopProfile/panels/TogglesPanel';
import { LivePreview } from '../components/shopProfile/LivePreview';

const ShopProfileInner = () => {
  const { profile } = useShell();
  const shopId = profile?.shop_id;

  const [original, setOriginal] = useState(null);   // last-saved snapshot of shop fields
  const [form, setForm]         = useState(null);   // working state for shop fields
  const [barbers, setBarbers]   = useState([]);     // read-only, used by the right-hand LivePreview
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  const [subStatus, setSubStatus] = useState('idle'); // idle|checking|ok|taken|invalid
  const [logoUploading, setLogoUploading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [heroUploading, setHeroUploading] = useState(false);

  // Services + hours used by both the right-hand live preview and the
  // 12 theme-picker thumbnails — loaded once, shared.
  const [pickerServices, setPickerServices] = useState([]);
  const [pickerHours, setPickerHours] = useState([]);

  // ===== Load shop + barbers + services + hours =====
  useEffect(() => {
    if (!shopId) return;
    (async () => {
      const [{ data: shopData, error: e1 }, { data: barberData, error: e2 }, { data: svcData }] = await Promise.all([
        supabase.from('shops').select('*').eq('id', shopId).single(),
        supabase.from('barbers').select('*').eq('shop_id', shopId).eq('active', true).order('created_at'),
        supabase.from('services').select('id,name,price,duration_min,description').eq('shop_id', shopId).eq('active', true).order('sort_order'),
      ]);
      if (e1) { setError(e1.message); setLoading(false); return; }
      if (e2) { setError(e2.message); setLoading(false); return; }
      setPickerServices(svcData || []);

      // Shop opening hours: prefer the explicit `shops.opening_hours`
      // (M4 editor below). If NULL — legacy shop that never opened the
      // profile editor — fall back to the union-of-barber-hours so the
      // preview still shows something familiar.
      let openingHours;
      if (Array.isArray(shopData.opening_hours) && shopData.opening_hours.length) {
        openingHours = normaliseOpeningHours(shopData.opening_hours);
      } else {
        const barberIds = (barberData || []).map(b => b.id);
        let byDay = {};
        if (barberIds.length) {
          const { data: bhrs } = await supabase
            .from('barber_hours').select('weekday,start_time,end_time,is_open')
            .in('barber_id', barberIds);
          for (const r of (bhrs || [])) {
            if (!r.is_open) continue;
            const d = r.weekday;
            if (!byDay[d]) byDay[d] = { start: r.start_time, end: r.end_time };
            else {
              if (r.start_time < byDay[d].start) byDay[d].start = r.start_time;
              if (r.end_time   > byDay[d].end)   byDay[d].end   = r.end_time;
            }
          }
        }
        openingHours = DAY_ORDER.map((d) => ({
          weekday: d,
          open_time: (byDay[d]?.start || '09:00').slice(0, 5),
          close_time: (byDay[d]?.end  || '18:00').slice(0, 5),
          closed: !byDay[d],
        }));
      }
      setPickerHours(openingHoursToDisplay(openingHours));

      const snap = {
        name: shopData.name || '',
        sms_display_name: shopData.sms_display_name || '',
        subdomain: shopData.subdomain || '',
        tagline: shopData.tagline || '',
        welcome_text: shopData.welcome_text || '',
        address: shopData.address || '',
        // M5 — structured address fields (postcode is mandatory once
        // any structured field is filled). Legacy shops that only have
        // `address` set land here with the four fields empty; the UI
        // shows a migration nudge and keeps rendering the free-text
        // block on the shop-front until they save the structured ones.
        address_line_1: shopData.address_line_1 || '',
        address_line_2: shopData.address_line_2 || '',
        city: shopData.city || '',
        postcode: shopData.postcode || '',
        logo_url: shopData.logo_url || '',
        accent_colour: shopData.accent_colour || '#4f46ff',
        gallery_urls: Array.isArray(shopData.gallery_urls) ? shopData.gallery_urls : [],
        // Hero image + opacity (add-hero-image.sql). Legacy shops with
        // the migration not yet applied land here with both null → we
        // treat that as "no hero" (opacity defaults to 1.0 so the
        // slider has a meaningful initial position).
        hero_image_url: shopData.hero_image_url || '',
        hero_opacity: shopData.hero_opacity != null ? Number(shopData.hero_opacity) : 1.0,
        // Booking page — new-style per-shop toggle. Defaults to FALSE
        // so the booking page shows its own theme background unless
        // the operator explicitly opts in.
        booking_inherit_hero: !!shopData.booking_inherit_hero,
        reviews: normaliseReviews(shopData.reviews),
        reviews_source: shopData.reviews_source === 'google' ? 'google' : 'instacut',
        google_place_id: shopData.google_place_id || '',
        deposit_required: !!shopData.deposit_required,
        deposit_type: shopData.deposit_type || 'fixed',
        deposit_amount: shopData.deposit_amount != null ? Number(shopData.deposit_amount) : '',
        show_hours: shopData.show_hours !== false,
        allow_choose_barber: shopData.allow_choose_barber !== false,
        theme: normaliseTheme(shopData.theme),
        social_links: shopData.social_links || {},
        opening_hours: openingHours,
        cancellation_notice_hours: Number.isFinite(Number(shopData.cancellation_notice_hours))
          ? Number(shopData.cancellation_notice_hours)
          : 24,
        timezone: shopData.timezone || 'Europe/London',
      };
      setOriginal(snap);
      setForm(snap);
      const bsnap = (barberData || []).map(b => ({
        id: b.id,
        name: b.name,
        role: b.role || '',
        bio: b.bio || '',
        photo_url: b.photo_url || '',
      }));
      setBarbers(bsnap);
      setLoading(false);
    })();
  }, [shopId]);

  // ===== Subdomain uniqueness check =====
  useEffect(() => {
    if (!form || !shopId) return;
    const sd = (form.subdomain || '').trim().toLowerCase();
    if (sd === '') { setSubStatus('idle'); return; }
    if (!SUBDOMAIN_RE.test(sd)) { setSubStatus('invalid'); return; }
    if (original && sd === original.subdomain) { setSubStatus('ok'); return; }
    setSubStatus('checking');
    const t = setTimeout(async () => {
      const { data } = await supabase
        .from('shops').select('id').eq('subdomain', sd).neq('id', shopId).maybeSingle();
      setSubStatus(data ? 'taken' : 'ok');
    }, 350);
    return () => clearTimeout(t);
  }, [form?.subdomain, shopId, original]);

  const isDirty = useMemo(() => {
    if (!original || !form) return false;
    if (JSON.stringify(original) !== JSON.stringify(form)) return true;
    return false;
  }, [original, form]);

  const canSave = isDirty
    && (subStatus === 'ok' || subStatus === 'idle')
    && !saving
    && form?.name?.trim();

  const onChange = (patch) => setForm(prev => ({ ...prev, ...patch }));

  const updateReview = (idx, patch) => {
    setForm(prev => ({
      ...prev,
      reviews: prev.reviews.map((r, i) => i === idx ? { ...r, ...patch } : r),
    }));
  };
  const addReview = () => {
    setForm(prev => ({ ...prev, reviews: [...prev.reviews, emptyReview()] }));
  };
  const deleteReview = (idx) => {
    setForm(prev => ({ ...prev, reviews: prev.reviews.filter((_, i) => i !== idx) }));
  };
  const toggleReviewHidden = (idx) => {
    setForm(prev => ({
      ...prev,
      reviews: prev.reviews.map((r, i) => i === idx ? { ...r, hidden: !r.hidden } : r),
    }));
  };
  const [googlePreview, setGooglePreview] = useState(null); // {rating,total} or {error}
  const [googleTesting, setGoogleTesting] = useState(false);
  const testGoogleReviews = async () => {
    setGoogleTesting(true);
    setGooglePreview(null);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      // Save pending id first so the endpoint reads it. Simplest: pass
      // via query param would need a new endpoint; instead persist a
      // best-effort update to the row then hit /google-reviews.
      await supabase.from('shops').update({
        reviews_source: 'google',
        google_place_id: (form.google_place_id || '').trim(),
      }).eq('id', shopId);
      const res = await fetch(`${BACKEND_URL}/api/shops/${shopId}/google-reviews`);
      const data = await res.json();
      if (!res.ok) {
        setGooglePreview({ error: data.detail || `HTTP ${res.status}` });
      } else {
        setGooglePreview({ rating: data.rating, total: data.total, url: data.url });
      }
    } catch (e) {
      setGooglePreview({ error: e.message });
    } finally {
      setGoogleTesting(false);
    }
  };

  // ===== File uploads =====
  const logoInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const heroInputRef = useRef(null);

  const uploadFile = async (file, pathPrefix) => {
    const ext = (file.name.split('.').pop() || 'png').toLowerCase();
    const key = `${shopId}/${pathPrefix}-${Date.now()}.${ext}`;
    const { error: ue } = await supabase.storage
      .from('shop-assets').upload(key, file, { upsert: true, contentType: file.type });
    if (ue) throw ue;
    const { data } = supabase.storage.from('shop-assets').getPublicUrl(key);
    return data.publicUrl;
  };

  const handleLogoFile = async (file) => {
    if (!file) return;
    setError(''); setLogoUploading(true);
    try {
      const url = await uploadFile(file, 'logo');
      onChange({ logo_url: url });
    } catch (e) { setError(`Logo upload failed: ${e.message}`); }
    finally { setLogoUploading(false); }
  };

  const handleGalleryFiles = async (files) => {
    if (!files || !files.length) return;
    // Hard-reject the batch if ANY file exceeds 3MB — matches the
    // hero-image cap on the server. Users see one clear message rather
    // than a partial upload where 2 of 3 photos land silently.
    const oversized = Array.from(files).filter(f => f.size > MAX_UPLOAD_BYTES);
    if (oversized.length) {
      setError('Each photo must be under 3MB');
      if (galleryInputRef.current) galleryInputRef.current.value = '';
      return;
    }
    setError(''); setGalleryUploading(true);
    try {
      const urls = [];
      for (const f of Array.from(files)) urls.push(await uploadFile(f, 'gallery'));
      onChange({ gallery_urls: [...(form.gallery_urls || []), ...urls] });
    } catch (e) { setError(`Photo upload failed: ${e.message}`); }
    finally {
      setGalleryUploading(false);
      if (galleryInputRef.current) galleryInputRef.current.value = '';
    }
  };

  const removeGalleryImage = (url) => {
    onChange({ gallery_urls: form.gallery_urls.filter(u => u !== url) });
  };

  // ===== Hero image =====
  //
  // Hero uploads / deletes go through the backend so we can:
  //   (a) enforce the 3MB cap server-side,
  //   (b) authorise per-shop (master_admin OR the shop's own admin),
  //   (c) clean up the previous Storage object atomically.
  //
  // Opacity is a pure DB write — it lives in `form.hero_opacity` and
  // persists on Publish, same as any other field.
  const heroAuthHeaders = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token
      ? { Authorization: `Bearer ${session.access_token}` }
      : {};
  };

  const handleHeroFile = async (file) => {
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      setError('Each photo must be under 3MB');
      if (heroInputRef.current) heroInputRef.current.value = '';
      return;
    }
    setError(''); setHeroUploading(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/shops/${shopId}/upload-hero`, {
        method: 'POST',
        headers: await heroAuthHeaders(),
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      // Persist to both `form` (so the preview refreshes immediately)
      // and `original` (so the dirty-check doesn't flag hero as an
      // unsaved change — the DB is already the source of truth).
      const nextForm = { ...form, hero_image_url: data.hero_image_url };
      setForm(nextForm);
      setOriginal(prev => prev ? { ...prev, hero_image_url: data.hero_image_url } : prev);
    } catch (e) {
      setError(`Hero upload failed: ${e.message}`);
    } finally {
      setHeroUploading(false);
      if (heroInputRef.current) heroInputRef.current.value = '';
    }
  };

  const handleHeroDelete = async () => {
    setError(''); setHeroUploading(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${BACKEND_URL}/api/shops/${shopId}/hero`, {
        method: 'DELETE',
        headers: await heroAuthHeaders(),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      setForm(prev => ({ ...prev, hero_image_url: '' }));
      setOriginal(prev => prev ? { ...prev, hero_image_url: '' } : prev);
    } catch (e) {
      setError(`Hero delete failed: ${e.message}`);
    } finally {
      setHeroUploading(false);
    }
  };

  // ===== Save =====
  //
  // M5 — postcode is enforced client-side. If the shop admin has
  // touched ANY structured address field, `postcode` becomes required
  // and must match the loose UK postcode pattern below. Legacy shops
  // that leave all four fields empty pass validation (the free-text
  // `address` block still renders on the shop-front). On successful
  // save we also re-assemble a canonical `address` string so any
  // theme/consumer still reading the free-text column keeps working.
  const buildAddressBlock = () => {
    // Only assemble from structured fields when at least line_1 is set.
    // Otherwise keep the legacy free-text address so nothing regresses.
    if (!form.address_line_1?.trim()) return form.address?.trim() || null;
    const parts = [
      form.address_line_1?.trim(),
      form.address_line_2?.trim(),
      form.city?.trim(),
      form.postcode?.trim().toUpperCase(),
    ].filter(Boolean);
    return parts.join(', ');
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!canSave) return;

    // Validate structured address if any of the fields are touched.
    const anyStructured = !!(
      form.address_line_1?.trim() ||
      form.address_line_2?.trim() ||
      form.city?.trim() ||
      form.postcode?.trim()
    );
    if (anyStructured) {
      if (!form.address_line_1?.trim()) {
        setError('Please enter your building number/name + street on line 1.');
        return;
      }
      if (!form.city?.trim()) {
        setError('Please enter your town or city.');
        return;
      }
      if (!form.postcode?.trim()) {
        setError('Postcode is required.');
        return;
      }
      if (!UK_POSTCODE_RE.test(form.postcode.trim())) {
        setError('That doesn\u2019t look like a UK postcode — double-check the format (e.g. M1 1AA).');
        return;
      }
    }

    setSaving(true); setError('');
    try {
      // Reviews (new shape). Filter empties; keep hidden flag; wipe
      // any legacy `quote`/`author` keys that might still be around.
      const cleanReviews = form.reviews
        .map(r => ({
          id: r.id,
          name: (r.name || '').trim(),
          review: (r.review || '').trim(),
          rating: Math.max(1, Math.min(5, Number(r.rating) || 5)),
          hidden: !!r.hidden,
        }))
        .filter(r => r.review);

      const shopPayload = {
        name: form.name.trim(),
        sms_display_name: form.sms_display_name?.trim() || null,
        subdomain: form.subdomain.trim().toLowerCase(),
        tagline: form.tagline?.trim() || null,
        welcome_text: form.welcome_text,
        // Structured address (M5)
        address_line_1: form.address_line_1?.trim() || null,
        address_line_2: form.address_line_2?.trim() || null,
        city: form.city?.trim() || null,
        postcode: form.postcode?.trim().toUpperCase() || null,
        // Legacy free-text `address` — re-assembled from structured fields
        // when present so map embed / theme text-blob consumers keep working.
        address: buildAddressBlock(),
        logo_url: form.logo_url || null,
        accent_colour: resolveAccent(form.theme, form.accent_colour),
        gallery_urls: form.gallery_urls,
        // hero_image_url is written by the upload/delete endpoints, not
        // by Publish — persist only the opacity here so a save can't
        // accidentally clobber the pointer. Clamp to the DB CHECK.
        hero_opacity: Math.max(0.1, Math.min(1.0, Number(form.hero_opacity ?? 1.0))),
        booking_inherit_hero: !!form.booking_inherit_hero,
        reviews: cleanReviews,
        reviews_source: form.reviews_source === 'google' ? 'google' : 'instacut',
        google_place_id: (() => {
          const raw = (form.google_place_id || '').trim();
          // Only persist strings that look like real Place IDs.
          // Rejects OAuth callback strings and other pastes that had
          // been landing in this column (test iteration_61).
          return /^[A-Za-z0-9_-]{20,}$/.test(raw) ? raw : null;
        })(),
        deposit_required: form.deposit_required,
        deposit_type: form.deposit_required ? (form.deposit_type || 'fixed') : 'fixed',
        deposit_amount: form.deposit_required ? Number(form.deposit_amount || 0) : 0,
        show_hours: form.show_hours,
        allow_choose_barber: form.allow_choose_barber,
        theme: normaliseTheme(form.theme),
        opening_hours: form.opening_hours,
        cancellation_notice_hours: Math.max(
          0,
          Math.min(168, Number(form.cancellation_notice_hours ?? 24)),
        ),
        timezone: (form.timezone && form.timezone.trim()) || 'Europe/London',
      };
      const { error: ue } = await supabase
        .from('shops').update(shopPayload).eq('id', shopId);
      if (ue) {
        // Graceful window: if any newer column (add-hero-image.sql /
        // add-reviews-rework.sql) hasn't been applied yet, Postgres
        // returns 42703 naming the offending column. Peel that column
        // out of the payload and retry — up to a small number of times
        // so a multi-column-missing case (fresh clone without either
        // migration) still lands.
        const stripMissing = async (payload, err) => {
          // Two distinct shapes of "column missing" the frontend can
          // hit:
          //   • Postgres 42703 —
          //       `column "shops.foo" does not exist`   (raw SQL)
          //   • PostgREST PGRST204 —
          //       `Could not find the 'foo' column of 'shops'
          //        in the schema cache`                 (via supabase-js)
          // Both mean the same thing (the migration hasn't landed yet).
          // Peel that column out and retry.
          const code = err.code || '';
          const msg = String(err.message || '');
          const isMissingCol = code === '42703' || code === 'PGRST204' ||
                               msg.includes('does not exist') ||
                               msg.includes('schema cache');
          if (!isMissingCol) throw err;
          const m = msg.match(/column\s+"?[\w.]*?\.?([a-z_0-9]+)"?\s+does not exist/i)
                 || msg.match(/find the ['"]?([a-z_0-9]+)['"]?\s+column/i);
          if (!m) throw err;
          const col = m[1];
          if (!(col in payload)) throw err;
          const next = { ...payload };
          delete next[col];
          console.warn(`[ShopProfile] shops.${col} missing — retrying Publish without it. Apply pending migrations to remove this warning.`);
          const { error: retryErr } = await supabase.from('shops').update(next).eq('id', shopId);
          if (retryErr) return stripMissing(next, retryErr);
          return null;
        };
        const finalErr = await stripMissing(shopPayload, ue).catch(e => e);
        if (finalErr) throw finalErr;
      }

      // Barber bio/photo persistence moved to /barbers (Edit modal).
      // ShopProfile only writes shop-level fields now.

      // Build a single post-save snapshot and use it for BOTH form and
      // original so the dirty-check can never disagree. Optional text
      // fields are kept as '' (not null) to match how the form holds them.
      const postSnap = {
        ...form,
        tagline: shopPayload.tagline || '',
        welcome_text: shopPayload.welcome_text || '',
        address: shopPayload.address || '',
        address_line_1: shopPayload.address_line_1 || '',
        address_line_2: shopPayload.address_line_2 || '',
        city: shopPayload.city || '',
        postcode: shopPayload.postcode || '',
        logo_url: shopPayload.logo_url || '',
        hero_image_url: form.hero_image_url || '',
        hero_opacity: shopPayload.hero_opacity,
        booking_inherit_hero: shopPayload.booking_inherit_hero,
        deposit_type: shopPayload.deposit_type,
        deposit_amount: shopPayload.deposit_amount,
        reviews: normaliseReviews(cleanReviews),
        theme: shopPayload.theme,
      };
      setOriginal(postSnap);
      setForm(postSnap);
    } catch (e) {
      setError(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return (
      <main className="main">
        <div className="topbar"><div><h2>Shop profile</h2></div></div>
        <div className="body"><div style={{ padding: 40, textAlign: 'center', color: 'var(--mute)' }}>Loading…</div></div>
      </main>
    );
  }

  return (
    <main className="main shop-profile">
      <div className="topbar">
        <div>
          <h2>Shop profile</h2>
          <div className="sub">This is what customers see at your booking link</div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 12, alignItems: 'center' }}>
          <span className={`saved-tag ${isDirty ? 'unsaved' : ''}`} data-testid="saved-tag">
            <span className="dot" />
            {isDirty ? 'Unsaved changes' : 'Saved'}
          </span>
          <button
            className="btn g"
            disabled={!canSave}
            onClick={handleSave}
            data-testid="publish-btn"
          >
            {saving ? 'Publishing…' : 'Publish changes'}
          </button>
        </div>
      </div>

      <div className="body">
        {error && <div className="modal-error" data-testid="profile-error" style={{ marginBottom: 12 }}>{error}</div>}

        <div className="edit-grid">
          {/* LEFT — editor panels */}
          <div>
            <IdentityPanel form={form} onChange={onChange} subStatus={subStatus} />

            <ThemePickerPanel
              form={form}
              onChange={onChange}
              barbers={barbers}
              pickerServices={pickerServices}
              pickerHours={pickerHours}
            />

            <LogoBrandPanel
              form={form}
              onChange={onChange}
              logoUploading={logoUploading}
              logoInputRef={logoInputRef}
              handleLogoFile={handleLogoFile}
            />

            <HeroImagePanel
              form={form}
              onChange={onChange}
              heroUploading={heroUploading}
              heroInputRef={heroInputRef}
              handleHeroFile={handleHeroFile}
              handleHeroDelete={handleHeroDelete}
            />

            <BookingPagePanel form={form} onChange={onChange} />

            {/* Barber bios & photos removed — now edited per-barber in
                the "Edit" modal on /barbers so shops manage one profile
                surface per barber instead of two. */}

            <GalleryPanel
              form={form}
              galleryUploading={galleryUploading}
              galleryInputRef={galleryInputRef}
              handleGalleryFiles={handleGalleryFiles}
              removeGalleryImage={removeGalleryImage}
            />

            <ReviewsPanel
              form={form}
              setForm={setForm}
              googlePreview={googlePreview}
              googleTesting={googleTesting}
              testGoogleReviews={testGoogleReviews}
              updateReview={updateReview}
              addReview={addReview}
              deleteReview={deleteReview}
              toggleReviewHidden={toggleReviewHidden}
            />

            <LocationPanel form={form} onChange={onChange} />

            <BusinessHoursPanel form={form} onChange={onChange} />

            <CancellationPolicyPanel form={form} onChange={onChange} />

            <TogglesPanel form={form} onChange={onChange} pickerServices={pickerServices} />
          </div>

          {/* RIGHT — live phone preview (renders the picked theme) */}
          <LivePreview
            form={form}
            barbers={barbers}
            services={pickerServices}
            hours={openingHoursToDisplay(form.opening_hours || [])}
          />
        </div>
      </div>
    </main>
  );
};

const ShopProfile = () => (
  <AppShell>
    <ShopProfileInner />
  </AppShell>
);

export default ShopProfile;
